import { MessageDescriptor } from 'react-intl'

import { CSSObject } from 'styled-components'

export enum ESSENTIALS_CONTENT_CATEGORY {
  ARTICLE = 'article',
  TOPIC = 'topic',
  TOOL = 'tool',
  COURSE = 'course',
  EVENT = 'event',
}

export enum ESSENTIALS_SITE_CATEGORY {
  ESSENTIALS = 'essentials',
  WORK_HUB = 'work-hub',
}

export enum ESSENTIALS_CONTENT_TYPE {
  MEDITATION = 'meditation',
  BREATHING_EXERCISE = 'breathing exercise',
}

export enum ESSENTIALS_CONTENT_FORMAT {
  AUDIO = 'audio',
  VIDEO = 'video',
}

export enum ESSENTIALS_CATEGORY {
  PARENTING_SUPPORT = 'parenting-support',
  STRENGTHENING_RELATIONSHIPS = 'strengthening-relationships',
}

export enum ESSENTIALS_SUBCATEGORY {
  NEW_FRIENDS_AND_RELATIONSHIPS = 'new-friends-and-relationships',
}

export enum ESSENTIALS_COURSE {
  PARENTING_IN_THE_REAL_WORLD = 'parenting-in-the-real-world',
  CURBING_YOUR_BURNOUT = 'curbing-your-burnout',
  NOTICE_AND_RESPOND_SUPPORTING_MENTAL_HEALTH_AT_WORK = 'notice-and-respond-supporting-mental-health-at-work',
  LEARNING_TO_MEDITATE = 'learning-to-meditate',
  MINDFUL_RELATIONSHIPS = 'mindful-relationships',
  CREATING_SAFER_WORKPLACES_TRAUMA_AND_TRIGGERS = 'creating-safer-workplaces-trauma-and-triggers',
}

export interface OnEssentialsCardPressParams {
  title: string
  subPath: string
  contentFormat: string
  contentType: string
  destinationSlug: string
  locale: string
  contentId?: string
  contentTopics?: string[]
  contentSubtopics?: string[]
  contentTools?: string[]
  contentSubtools?: string[]
  index?: number
  isLyraSelect?: boolean
  isEnglish?: boolean
}

/** Callback function for pressing on essentials content */
export type OnEssentialsCardPressFunction = ({
  title,
  subPath,
  contentFormat,
  contentType,
  destinationSlug,
  locale,
  contentTopics,
  contentSubtopics,
  contentTools,
  contentSubtools,
}: OnEssentialsCardPressParams) => void

/** Article data returned from goodness apis */
export interface EssentialsArticleRaw {
  title: string
  uri: string
  image: { url: string }[]
  contentType: string
  readTime: string
  id: number
  contentFormat: string
  category?: {
    slug: string
    title: string
    type: string
    parent?: {
      slug: string
      title: string
      type: string
    }
  }[]
  isEnglish?: boolean
}

/** Course data returned from goodness apis */
export interface EssentialsCourseRaw {
  title: string
  uri: string
  contentType: string
  foreground: { url: string }[]
  id: number
  slug: string
  subcategories: { id: number; title: string }[]
  isEnglish?: boolean
}

/** Essentials content data from goodness apis after being processed and formatted */
export interface EssentialsContentFormatted {
  title: string
  imageUrl: string
  contentType: string
  readTime: string
  id?: number
  contentFormat: string
  categoryType?: ESSENTIALS_CONTENT_CATEGORY
  onPress: ({ isLyraSelect, index }: Partial<OnEssentialsCardPressParams>) => void
  uri?: string
  isEnglish?: boolean
}

export type EssentialsContentRaw = EssentialsArticleRaw | EssentialsCourseRaw

export interface EssentialsContentData {
  contentFormat: string
  contentType: string
  id: string
  slug: string
  title: string
  isWorkHub?: boolean
}

export enum ESSENTIALS_HOME_CATEGORY {
  MEDITATIONS = 'meditations',
  COURSES = 'courses',
  BREATHING_EXERCISES = 'breathing exercises',
  VIRTUAL_EVENTS = 'virtual events',
  SOUNDSCAPES = 'soundscapes',
}

/**
 * Entry tags for Craft CMS content filtering and categorization.
 *
 * These tags are used by Craft CMS's tagging system to filter and categorize
 * various types of content including events, articles, and categories. The tags
 * are specifically used to query and organize Essentials content from the microsite
 * builder system.
 *
 * Note: These tags are content-agnostic and not specific to wellness toolkits.
 * Examples include: adult-only, recurring, featured, etc.
 */
export enum ESSENTIALS_ENTRY_TAG {
  NEURODIVERSITY = 'neurodiversity',
  WOMENS_HEALTH = 'womens-health',
  HEALTH_CHALLENGES = 'health-challenges',
}

interface EssentialsEventTimeData {
  eventDate: Date
  /** Zoom id (not the same as EssentialsEventData id) */
  eventId: string
}

export type EssentialsEventType = 'discuss' | 'watch' | 'meditate'

export interface EssentialsEventDataApiResponse {
  id: string
  uri: string
  slug: string
  title: string
  tags?: ESSENTIALS_ENTRY_TAG[]
  eventType: EssentialsEventType
  eventSeries: boolean
  eventTimes: { eventDate: string; eventId: string }[]
  eventSeriesTimes: { eventDate: string; eventId: string }[]
  seatCount: number
  facilitators: {
    facilitatorName: string
    photo: {
      url: string
    }[]
  }[]
}

export interface EssentialsEventData {
  id: string
  uri: string
  slug: string
  title: string
  eventType: EssentialsEventType
  eventSeries: boolean
  eventTimes: EssentialsEventTimeData[]
  eventSeriesTimes: EssentialsEventTimeData[]
  seatCount: number
  facilitators: {
    facilitatorName: string
    photo: {
      url: string
    }[]
    mainPhoto: string
  }[]
}

export interface EssentialsUpcomingEventData extends EssentialsEventData {
  isRegistered: boolean
}

export interface EssentialsRegisteredEventData {
  id: string
  timeStamp: {
    eventDate: Date
    eventTitle: string
    /** Zoom id */
    eventId: string
    facilitatorName: string
    facilitatorImage?: {
      url: string
    }[]
    registrant_id?: string
    join_url?: string
  }
}

export enum ZOOM_EVENT_TYPE {
  MEETING = 'MEETING',
  WEBINAR = 'WEBINAR',
  EVENT = 'EVENT',
}

export const zoomEventTypeMap: Record<EssentialsEventType, ZOOM_EVENT_TYPE> = {
  watch: ZOOM_EVENT_TYPE.WEBINAR,
  discuss: ZOOM_EVENT_TYPE.MEETING,
  meditate: ZOOM_EVENT_TYPE.WEBINAR,
}

export interface useNavigateToEssentialsContentProps {
  slug?: string
  entryPoint?: string
  category?: ESSENTIALS_CONTENT_CATEGORY
  subPath?: string
  locale?: string
  activeTabOverride?: string
  toggleTranscript?: string
  /** Sets `referrer` prop in mixpanel events sent from goodness */
  referrer?: string
  /** Events + props to send to goodness so they can be sent to mixpanel */
  mixpanelEventsAddon?: Dict
  site?: ESSENTIALS_SITE_CATEGORY
  /** Sets `referrer_page` prop in mixpanel view essentials event */
  referrerPage?: string
  /** Whether the user is logged in - used to determine routing */
  loggedIn?: boolean
  shouldOpenOrientationMixes?: boolean
  /** For 1lyra web, sometimes going to home screen + essentials screen will navigate to secure/index?screen=Essentials instead of secure/index/essentials. Use this prop to navigate directly to essentials */
  navigateDirectly?: boolean
}

export type HomebaseEssentialsStorageContents = {
  contents: EssentialsContentRaw[]
  expirationTimestamp: number
}

export type EssentialsContentSection = {
  title: MessageDescriptor
  content: EssentialsContentFormatted[]
  headerLink?: React.ReactElement
}

export enum ESSENTIALS_CMS_QUERY_APP_ENV {
  DEV = 'dev',
  PROD = 'prod',
}

export interface EssentialsUpcomingEventsStyles {
  essentialsUpcomingEventsContainer: CSSObject
  essentialsEventCardRegisteredTagContainer: CSSObject
}
